import { useState, useEffect, useCallback } from 'react';

import useGetTrapsActivityLineChart from './useGetTrapsActivityLineChart';

import { Period } from 'components/AnalyticsPage/Periods';
import { TrapType } from 'components/AnalyticsPage/TrapType';

interface ChartData {
  date: string[];
  mct_count?: number[];
  smst_count?: number[];
  srst_count?: number[];
}

interface UseChartDataProps {
  slug: string;
  trapType: TrapType;
}

export const useChartData = ({ slug, trapType }: UseChartDataProps) => {
  const [period, setPeriod] = useState<Period>(Period.THREE_MONTH);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [showCustom, setShowCustom] = useState<boolean>(true);

  const { data, refetch, isLoading, isFetching } = useGetTrapsActivityLineChart(
    slug,
    period,
    trapType,
    startDate,
    endDate,
    false,
  );

  const [chartData, setChartData] = useState<ChartData>({
    date: [],
  });

  useEffect(() => {
    if (!data || isFetching) {
      setChartData({ date: [], mct_count: [], smst_count: [], srst_count: [] });
      return;
    }

    const date = data.map((item: any) => item.date);

    if (trapType === TrapType.SRST) {
      const srst_count = data.map((item: any) => item.srst_count ?? 0);
      setChartData({ date, srst_count });
    } else if (trapType === TrapType.SMST) {
      const smst_count = data.map((item: any) => item.smst_count ?? 0);
      setChartData({ date, smst_count });
    } else if (trapType === TrapType.MCT) {
      const mct_count = data.map((item: any) => item.mct_count ?? 0);
      setChartData({ date, mct_count });
    } else {
      const mct_count = data.map((item: any) => item.mct_count ?? 0);
      const smst_count = data.map((item: any) => item.smst_count ?? 0);
      setChartData({ date, mct_count, smst_count });
    }
  }, [data, trapType, isFetching]);

  useEffect(() => {
    const currentPath = window.location.pathname;

    if (!currentPath.includes('analitics')) {
      return;
    }

    if (startDate && endDate) {
      const timer = setTimeout(() => {
        refetch();
      }, 1000);

      return () => clearTimeout(timer);
    } else if (period !== 'CUSTOM') {
      refetch();
    }
  }, [slug, trapType, startDate, endDate, period, refetch]);

  const handlePeriodChange = useCallback((newPeriod: Period) => {
    if (newPeriod === 'CUSTOM') {
      const today = new Date();
      const oneYearBefore = new Date();
      oneYearBefore.setFullYear(today.getFullYear() - 1);

      setStartDate(oneYearBefore.toISOString().split('T')[0]);
      setEndDate(today.toISOString().split('T')[0]);
      setShowCustom(false);
    } else {
      setStartDate('');
      setEndDate('');
      setShowCustom(true);
    }
    setPeriod(newPeriod);
  }, []);

  return {
    period,
    startDate,
    endDate,
    showCustom,
    chartData,
    isLoading,
    isFetching,
    handlePeriodChange,
    setStartDate,
    setEndDate,
    refetch,
  };
};
